import { Integration } from "/app/src/models";
import Box from "/app/src/components/generic/components/box";
import { useCallback, useMemo } from "react";
import { Form, Input, Select, SubmitButton } from "formik-antd";
import { useTranslation } from "react-i18next";
import { FieldArray, Formik, FormikHelpers, FormikProps } from "formik";
import type { FieldArrayRenderProps } from "formik";
import GridWrapper from "/app/src/components/generic/gridWrapper";
import useSetting from "/app/src/components/settings/setting";
import { Button } from "antd";
import { getDirectionTypeLabel } from "/app/src/helpers/data";

interface ExtraFilterKV {
  key: string;
  value: string;
}

interface FormValues {
  priority?: string[];
  directionType?: string[]; // stored as numeric strings
  extras: ExtraFilterKV[];
}

const KNOWN_KEYS = ["priority", "directionType"] as const;
type KnownKey = (typeof KNOWN_KEYS)[number];

/**
 * Normalize a value into an array.
 *
 * Returns an empty array when the input is `undefined`, returns the input unchanged
 * when it is already an array, and wraps a single value into a one-element array
 * otherwise.
 *
 * @typeParam T - The element type.
 * @param val - A value that may be a single `T`, an array of `T`, or `undefined`.
 * @returns An array of `T`.
 *
 * @example
 * ensureArray(42); // [42]
 * ensureArray([42]); // [42]
 * ensureArray(undefined); // []
 */
function ensureArray<T>(val: T | T[] | undefined): T[] {
  if (val === undefined) return [];
  return Array.isArray(val) ? val : [val];
}

// Standalone component to render and manage Advanced (extras) filters without inline JSX arrow handlers
function ExtrasArrayComponent({ form, push, remove }: FieldArrayRenderProps) {
  const { t } = useTranslation();
  const values = form.values as FormValues;

  const handleAddExtra = useCallback(() => {
    push({ key: "", value: "" });
  }, [push]);

  const handleRemoveExtra = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const idxAttr = e.currentTarget.getAttribute("data-index");
      const idx = idxAttr ? parseInt(idxAttr, 10) : NaN;
      if (!Number.isNaN(idx)) remove(idx);
    },
    [remove],
  );

  return values.extras && values.extras.length > 0 ? (
    <div className="mt-4">
      <h3 className="text-[16px] mb-2">
        {t("translation:advanced")} {t("translation:filters")}
      </h3>
      <div className="space-y-2">
        {values.extras.map((kv, index) => {
          const itemKey = `${kv.key ?? ""}::${kv.value ?? ""}`;
          return (
            <div key={itemKey} className="flex gap-2 items-start">
              <Form.Item
                name={`extras[${index}].key`}
                className="flex-1"
                hasFeedback={false}
                label={t("translation:key")}
              >
                <Input
                  name={`extras[${index}].key`}
                  size="large"
                  placeholder={t("translation:info1") as string}
                />
              </Form.Item>
              <Form.Item
                name={`extras[${index}].value`}
                className="flex-1"
                hasFeedback={false}
                label={t("translation:value")}
              >
                <Input
                  name={`extras[${index}].value`}
                  size="large"
                  placeholder={t("translation:enter_value") as string}
                />
              </Form.Item>
              <div className="mb-0" style={{ paddingTop: 30 }}>
                <Button
                  danger
                  type="default"
                  data-index={index}
                  data-key={itemKey}
                  onClick={handleRemoveExtra}
                  size="large"
                >
                  {t("translation:remove")}
                </Button>
              </div>
            </div>
          );
        })}
        <Button type="dashed" onClick={handleAddExtra}>
          + {t("translation:add")} {t("translation:filter")}
        </Button>
      </div>
    </div>
  ) : (
    <div className="mt-2">
      <Button type="dashed" onClick={handleAddExtra}>
        + {t("translation:add")} {t("translation:advanced")}{" "}
        {t("translation:filter")}
      </Button>
    </div>
  );
}

// Parse a query string like "priority=[or]1;2&directionType=3&custom=foo" into a map
function parseQueryString(
  queryString: string | undefined,
): Record<string, string | string[]> {
  if (!queryString) return {};
  return queryString
    .split("&")
    .reduce<Record<string, string | string[]>>((acc, pair) => {
      if (!pair) return acc;
      const [rawKey, rawVal = ""] = pair.split("=");
      const key = decodeURIComponent(rawKey || "");
      const decVal = decodeURIComponent(rawVal);
      if (!key) return acc;

      const isOr = decVal.startsWith("[or]");
      const values = isOr
        ? decVal
            .substring(4)
            .split(";")
            .filter((v) => v.length > 0)
        : [decVal];

      const existing = acc[key];
      if (existing === undefined) {
        acc[key] = isOr ? values : (values[0] ?? "");
      } else if (Array.isArray(existing)) {
        acc[key] = existing.concat(values);
      } else {
        acc[key] = [existing, ...values];
      }
      return acc;
    }, {});
}

/**
 * Encode a key and one or more values into a URL-style "key=value" string.
 *
 * - Each value in `values` is first encoded with `encodeURIComponent`.
 * - If `values` is empty, an empty string is returned.
 * - If `values` contains exactly one element, the result is:
 *     encodeURIComponent(key) + "=" + encodeURIComponent(value)
 *   (value is encoded once).
 * - If `values` contains more than one element, the result is:
 *     encodeURIComponent(key) + "=" + encodeURIComponent("[or]" + joined)
 *   where `joined` is the semicolon-separated list of the individually encoded values.
 *   Note: because each value is encoded before joining and the combined string is
 *   then passed through `encodeURIComponent` again, percent characters from the
 *   first encoding get re-encoded (i.e. values are effectively double-encoded).
 *
 * Preserves the order of `values`. Uses `;` as the delimiter for multiple values
 * and the literal prefix "[or]" to indicate an OR-list when multiple values are present.
 *
 * @param key - The query parameter name to encode.
 * @param values - An array of values to associate with the key.
 * @returns A string of the form "encodedKey=encodedValue" for one value,
 *          "encodedKey=encoded([or]encodedVal1;encodedVal2;...)" for multiple values,
 *          or an empty string if `values` is empty.
 *
 * @example
 * // Single value:
 * // key = "tag", values = ["hello world"]
 * // -> "tag=hello%20world" (key and value are URL-encoded)
 *
 * @example
 * // Multiple values:
 * // key = "tag", values = ["a", "b c"]
 * // -> "tag=%255Bor%255Da%253Bb%2520c"
 * // Explanation: each value is encoded ("a", "b%20c"), joined to "[or]a;b%20c",
 * // then the whole string is encoded again (percent signs become "%25", etc.).
 */
function encodeKeyValues(key: string, values: string[]): string {
  const clean = values.map((v) => encodeURIComponent(v));
  if (clean.length === 0) return "";
  if (clean.length === 1) return `${encodeURIComponent(key)}=${clean[0]}`;
  return `${encodeURIComponent(key)}=${encodeURIComponent(`[or]${clean.join(";")}`)}`;
}

/**
 * Builds a URL query string from the provided form values.
 *
 * The function:
 * - Includes `priority` and `directionType` arrays when they exist and are non-empty.
 * - Processes `extras` (an array of key/value pairs) by trimming whitespace,
 *   ignoring entries with empty keys or values, and grouping values by key.
 * - For each key (from `priority`, `directionType`, and grouped `extras`) it
 *   delegates to `encodeKeyValues` to produce an encoded segment.
 * - Joins the resulting segments with '&' and returns the final string.
 *
 * @param values - Form values containing optional arrays `priority` and `directionType`
 *                 and an `extras` array of objects with optional `key` and `value` strings.
 * @returns A URL query string formed by encoded key/value segments (for example:
 *          "priority=high&directionType=inbound&tag=foo&tag=bar"). Returns an empty
 *          string if no valid key/value pairs are present.
 *
 * @remarks
 * - Leading/trailing whitespace for `extras` keys and values is trimmed before grouping.
 * - Entries with empty keys or values after trimming are ignored.
 * - The actual encoding and formatting of each key's values is performed by `encodeKeyValues`.
 */
function buildQueryString(values: FormValues): string {
  const parts: string[] = [];

  if (values.priority && values.priority.length > 0) {
    parts.push(encodeKeyValues("priority", values.priority));
  }
  if (values.directionType && values.directionType.length > 0) {
    parts.push(encodeKeyValues("directionType", values.directionType));
  }

  const grouped = values.extras.reduce<Record<string, string[]>>((acc, kv) => {
    const trimmedKey = (kv.key || "").trim();
    const trimmedValue = (kv.value || "").trim();
    if (!trimmedKey || !trimmedValue) return acc;
    if (!acc[trimmedKey]) acc[trimmedKey] = [];
    acc[trimmedKey].push(trimmedValue);
    return acc;
  }, {});

  Object.entries(grouped).forEach(([k, vals]) => {
    parts.push(encodeKeyValues(k, vals));
  });

  return parts.filter(Boolean).join("&");
}

/**
 * GenericSettings
 *
 * A React component that renders and manages an order filter settings form for a given integration.
 * The component reads an existing filter query string from a persistent setting (via useSetting),
 * parses it into structured Formik initial values, allows the user to modify filters (priority,
 * directionType and arbitrary "extras"), previews the resulting query string, and persists changes
 * by creating/updating the underlying setting.
 *
 * Key behavior:
 * - Uses useSetting({ type: "orderFilters", integrationId }) to load and persist the setting value.
 * - Parses the stored query string into known keys (priority, directionType) and extra key/value pairs
 *   using parseQueryString and ensureArray.
 * - Builds a query string from current form values on submit using buildQueryString and persists it
 *   through createUpdateSetting.
 * - Uses Formik for form state, enableReinitialize to respond to changes in the loaded setting,
 *   and a FieldArray to manage arbitrary extra filters.
 * - Renders:
 *   - Multi-select for "priority"
 *   - Multi-select for "directionType"
 *   - Dynamic list of extra key/value pairs (managed by an ExtrasArrayComponent)
 *   - A live preview of the query string (or "(none)" when empty)
 * - Submit button is disabled unless the form is dirty and not submitting.
 *
 * Notes and invariants:
 * - The component expects a valid Integration object with an id. The id is used as a dependency
 *   for loading/saving settings; the useSetting hook is only invoked when integration.id is truthy.
 * - initialValues are memoized from the loaded settingValue to avoid unnecessary recomputation.
 * - onSubmit resets the Formik form to the submitted values after successful persistence.
 *
 * @param props.integration - The integration for which order filter settings are being configured.
 *                              The integration.id is used to scope the persisted setting.
 * @returns A JSX element containing the form for editing order filter settings.
 *
 * @example
 * <GenericSettings integration={integration} />
 *
 * @remarks
 * - Side effects: reads and updates persistent settings via the useSetting hook.
 * - Localization: UI strings are resolved via useTranslation().
 */
export default function GenericSettings({
  integration,
}: {
  integration: Integration;
}) {
  const { t } = useTranslation();

  const { settingValue, createUpdateSetting } = useSetting(
    { type: "orderFilters", integrationId: integration.id },
    Boolean(integration?.id),
  );

  const initialValues: FormValues = useMemo(() => {
    const parsed = parseQueryString(settingValue);

    const initialKnown: Partial<Record<KnownKey, string[]>> = {};
    KNOWN_KEYS.forEach((key) => {
      const value = parsed[key];
      if (value !== undefined) initialKnown[key] = ensureArray(value);
    });

    const extras: ExtraFilterKV[] = Object.entries(parsed)
      .filter(([k]) => !KNOWN_KEYS.includes(k as KnownKey))
      .flatMap(([key, value]) =>
        Array.isArray(value)
          ? (value as string[]).map((v) => ({ key, value: v }))
          : [{ key, value: value as string }],
      );

    return {
      priority: initialKnown.priority || undefined,
      directionType: initialKnown.directionType || undefined,
      extras,
    } as FormValues;
  }, [settingValue]);

  const onSubmit = useCallback(
    async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      const queryString = buildQueryString(values);
      await createUpdateSetting({
        value: queryString,
        type: "orderFilters",
        integrationId: integration.id,
      });
      actions.resetForm({ values });
    },
    [createUpdateSetting, integration.id],
  );

  const settingsForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isSubmitting, values }) => {
        return (
          <Form layout="vertical">
            <div className="flex justify-between items-center">
              <h2 className="text-[18px]">{t("translation:order_filters")}</h2>
              <SubmitButton type="primary" disabled={!dirty || isSubmitting}>
                {t("translation:save")}
              </SubmitButton>
            </div>

            <GridWrapper responsive={{ sm: 1, md: 1, lg: 3 }}>
              <Form.Item
                name="priority"
                label={t("translation:priority")}
                hasFeedback={false}
              >
                <Select
                  name="priority"
                  size="large"
                  mode="multiple"
                  allowClear
                  placeholder={t("translation:select") as string}
                >
                  <Select.Option key="1" value="1">
                    {t("translation:low")} (1)
                  </Select.Option>
                  <Select.Option key="2" value="2">
                    {t("translation:medium")} (2)
                  </Select.Option>
                  <Select.Option key="3" value="3">
                    {t("translation:high")} (3)
                  </Select.Option>
                  <Select.Option key="4" value="4">
                    {t("translation:hot")} (4)
                  </Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="directionType"
                label={t("translation:type")}
                hasFeedback={false}
              >
                <Select
                  name="directionType"
                  size="large"
                  mode="multiple"
                  allowClear
                  placeholder={t("translation:select") as string}
                >
                  {[1, 2, 3, 4, 5].map((dt) => (
                    <Select.Option key={dt} value={String(dt)}>
                      {getDirectionTypeLabel(dt) || dt}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </GridWrapper>

            <FieldArray name="extras" component={ExtrasArrayComponent} />

            <div className="mt-4">
              <div className="text-xs text-gray-500">
                {t("translation:order_filters")} preview:{" "}
                {buildQueryString(values) || "(none)"}
              </div>
            </div>
          </Form>
        );
      },
      [t],
    );

  return (
    <Box>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        enableReinitialize
        component={settingsForm}
      />
    </Box>
  );
}
